import { Link, useNavigate } from "react-router-dom";
import { FaEye, FaEdit, FaTrash, FaPlus } from "react-icons/fa";
import { toast } from "react-toastify";

import {
  useGetQuestionBankSubjectsQuery,
  useDeleteQuestionBankSubjectMutation,
} from "../../slices/questionBankSubjectApiSlice";

const QuestionBankSubjectListScreen = () => {
  const navigate = useNavigate();

  const {
    data: subjects,
    isLoading,
    error,
    refetch,
  } = useGetQuestionBankSubjectsQuery();

  const [deleteQuestionBankSubject, { isLoading: loadingDelete }] =
    useDeleteQuestionBankSubjectMutation();

  const deleteHandler = async (id) => {
    if (window.confirm("Are you sure you want to delete this subject?")) {
      try {
        await deleteQuestionBankSubject(id).unwrap();

        toast.success("Question bank subject deleted successfully");

        refetch();
      } catch (err) {
        toast.error(
          err?.data?.message ||
            err?.error ||
            "Failed to delete question bank subject",
        );
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 via-blue-50 to-indigo-100 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-800">
              Question Bank Subjects
            </h1>

            <p className="text-gray-500 mt-1">Manage Question Bank Subjects</p>
          </div>

          <Link
            to="/question-bank-subjects/create"
            className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-3 text-white font-semibold shadow-lg hover:from-blue-700 hover:to-indigo-700 transition"
          >
            <FaPlus />
            Create Subject
          </Link>
        </div>

        {/* Loading */}
        {(isLoading || loadingDelete) && (
          <div className="rounded-xl bg-blue-100 text-blue-700 p-4 mb-6 text-center">
            Loading...
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="rounded-xl bg-red-100 text-red-700 p-4 mb-6 text-center">
            {error?.data?.message || error?.error}
          </div>
        )}

        {/* Table */}
        {!isLoading && subjects?.length > 0 ? (
          <div className="overflow-x-auto rounded-3xl bg-white/80 backdrop-blur-lg shadow-2xl border border-white/30">
            <table className="min-w-full">
              <thead className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
                <tr>
                  <th className="px-6 py-4 text-left">#</th>

                  <th className="px-6 py-4 text-left">Subject</th>

                  <th className="px-6 py-4 text-left">Category</th>

                  <th className="px-6 py-4 text-left">Description</th>

                  <th className="px-6 py-4 text-left">Created By</th>

                  <th className="px-6 py-4 text-center">Actions</th>
                </tr>
              </thead>

              <tbody>
                {subjects.map((subject, index) => (
                  <tr
                    key={subject._id}
                    className="border-b hover:bg-blue-50 transition"
                  >
                    <td className="px-6 py-4">{index + 1}</td>

                    <td className="px-6 py-4 font-semibold text-gray-800">
                      {subject.name}
                    </td>

                    <td className="px-6 py-4">
                      <span className="rounded-full bg-indigo-100 px-3 py-1 text-sm text-indigo-700 font-medium">
                        {subject.category}
                      </span>
                    </td>

                    <td className="px-6 py-4 text-gray-600">
                      {subject.description || "-"}
                    </td>

                    <td className="px-6 py-4">
                      <div className="font-medium">
                        {subject.createdBy?.name || "-"}
                      </div>

                      <div className="text-sm text-gray-500">
                        {subject.createdBy?.phone}
                      </div>
                    </td>

                    <td className="px-6 py-4">
                      <div className="flex justify-center gap-2">
                        <button
                          onClick={() =>
                            navigate(`/question-bank-subjects/${subject._id}`)
                          }
                          className="rounded-lg bg-green-100 p-2 text-green-600 hover:bg-green-200 transition"
                          title="View"
                        >
                          <FaEye />
                        </button>

                        <button
                          onClick={() =>
                            navigate(
                              `/question-bank-subjects/${subject._id}/edit`,
                            )
                          }
                          className="rounded-lg bg-blue-100 p-2 text-blue-600 hover:bg-blue-200 transition"
                          title="Edit"
                        >
                          <FaEdit />
                        </button>

                        <button
                          onClick={() => deleteHandler(subject._id)}
                          className="rounded-lg bg-red-100 p-2 text-red-600 hover:bg-red-200 transition"
                          title="Delete"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          !isLoading && (
            <div className="rounded-3xl bg-white p-16 text-center shadow-xl">
              <h2 className="text-2xl font-semibold text-gray-700 mb-3">
                No Question Bank Subjects Found
              </h2>

              <p className="text-gray-500 mb-8">
                Create your first Question Bank Subject to get started.
              </p>

              <Link
                to="/question-bank-subjects/create"
                className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-3 text-white font-semibold hover:from-blue-700 hover:to-indigo-700 transition"
              >
                <FaPlus />
                Create Subject
              </Link>
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default QuestionBankSubjectListScreen;
